import { useEffect } from 'react';
import { OPTIMIZELY_PROJECT_KEY } from '../Utils/utils';

type WindowAny = Window & {
  optimizely?: any[];
  __optimizelyLoaded?: boolean;
  getConsentState?: () => any;
};

const DEFAULT_SRC = `https://cdn.optimizely.com/js/${OPTIMIZELY_PROJECT_KEY()}.js`;

export default function useOptimizelyInitialization(src: string = DEFAULT_SRC) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const w = window as WindowAny;

    w.optimizely = w.optimizely || [];
    w.__optimizelyLoaded = !!w.__optimizelyLoaded;

    function analyticsGranted(consent: any) {
      return !!(consent && consent.analytics === true);
    }

    // Synchronous decision at mount time if possible
    let state: any = null;
    try {
      if (typeof w.getConsentState === 'function') {
        state = w.getConsentState();
      }
    } catch (e) {
      state = null;
    }

    const initialGranted = analyticsGranted(state && state.consent);

    if (initialGranted && !w.__optimizelyLoaded) {
      w.__optimizelyLoaded = true;
      try {
        if (document.readyState === 'loading') {
          // try to mirror the original parser-blocking behaviour when possible
          // eslint-disable-next-line @typescript-eslint/no-implied-eval
          document.write('<script src="' + src + '"><\/script>');
        } else {
          const s = document.createElement('script');
          s.src = src;
          s.async = false;
          document.head.insertBefore(
            s,
            document.head.querySelector('meta:last-of-type')?.nextSibling || document.head.firstChild
          );
        }
      } catch (e) {
        const s = document.createElement('script');
        s.src = src;
        s.async = false;
        document.head.insertBefore(
          s,
          document.head.querySelector('meta:last-of-type')?.nextSibling || document.head.firstChild
        );
      }
    }

    function onConsentUpdated(e: Event) {
      const detail = (e as CustomEvent).detail;
      const granted = analyticsGranted(detail && detail.consent);

      if (granted && !w.__optimizelyLoaded) {
        w.__optimizelyLoaded = true;
        const s = document.createElement('script');
        s.src = src;
        s.async = false;
        document.head.insertBefore(
          s,
          document.head.querySelector('meta:last-of-type')?.nextSibling || document.head.firstChild
        );
      } else if (!granted && w.__optimizelyLoaded) {
        try {
          w.optimizely = w.optimizely || [];
          w.optimizely.push({ type: 'optOut', isOptOut: true });
        } catch (err) {
          // swallow
        }
      }
    }

    window.addEventListener('consentUpdated', onConsentUpdated as EventListener);

    return () => {
      window.removeEventListener('consentUpdated', onConsentUpdated as EventListener);
    };
  }, [src]);
}
